/* eslint-disable no-restricted-globals */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { message } from 'antd';

import {
  updateCaps,
  getOrgNameAndCageCode,
  querySupplierCapsForautoComplete,
  getSupplierCapabilities,
} from './EditSupplierCapsQL';
import './EditSupplierCaps.scss';
import updateCapabilities from './updateCapabilities';

// Local Deps

export default function EditSupplierHOC(EditSupplierPage) {
  class WrappedComponent extends Component {
    static propTypes = {
      auth: PropTypes.shape({
        jwtToken: PropTypes.string,
        expiration: PropTypes.number,
        organizationId: PropTypes.string,
      }).isRequired,
    };

    static initialState = {
      isLoading: true,
      initialValues: {
        additionOptions: null,
        additiveManufacturingCapabilities: [],
        additionalCapabilities: [],
        alloy: [],
        approvedSource: [],
        assemblyMethods: [],
        bizClass: [],
        castings: [],
        /** castingWeight: null, */
        componentWeight: null,
        minMaxDimensions: [],
        coreProcesses: [],
        dimensionCapability: [],
        forgingProcesses: [],
        fsc: [],
        heatCapacity: [],
        industriesServed: [],
        lowVolume: null,
        machiningEnvelope: [],
        machiningMethods: [],
        machiningServices: [],
        material: [],
        moldCastingProcess: [],
        nonDestructiveTesting: [],
        qualityCerts: [],
        rapidTooling: [],
        secondaryOperations: [],
        simulationCapability: null,
        surfaceTreatments: [],
        weightRange: null,
      },
      alloySteel: [],
      aluminum: [],
      other: [],
      copper: [],
      iron: [],
      steel: [],
      mmc: [],
      alloy: [],
      material: [],
      orgId: null,
    };

    state = WrappedComponent.initialState;

    async componentDidMount() {
      const { auth } = this.props;
      const res = await getOrgNameAndCageCode(auth.organizationId);

      let supCapsResp;
      let detailedCapabilities = null;

      try {
        // Get aggregated data for search/filtering
        supCapsResp = await querySupplierCapsForautoComplete({
          cage: res.organization.cageCode,
        });

        // Get detailed supplier capabilities including minMaxDimensions
        // Note: This requires the backend server to be running and proper authentication
        try {
          detailedCapabilities = await getSupplierCapabilities(res.organization.cageCode, this.props.auth);
          console.log('Detailed capabilities response:', detailedCapabilities);
        } catch (detailedError) {
          console.warn('Failed to fetch detailed capabilities, continuing without minMaxDimensions:', detailedError);
          console.warn('This is expected if the backend server is not running or authentication fails');
          detailedCapabilities = null;
        }
      } catch (error) {
        console.error("Error fetching supplier capabilities:", error);
        // Set initial state even when there's an error fetching data
        this.setState({
          initialValues: this._getInitialValues(res.organization),
        });
        await updateCaps(this._getInitialValues(res.organization)); // Ensure update is done even if fetch fails
        return;
      }

      const suppliers = supCapsResp;
      console.log('Full suppliers response:', suppliers);
      console.log('Component Weight data:', suppliers && suppliers.supplierAggs && suppliers.supplierAggs['Component Weight']);
      console.log('Supplier Capabilities data:', suppliers && suppliers.supplierCapabilities);

      // Check if suppliers and supplierAggs exist before proceeding
      if (!suppliers || !suppliers.supplierAggs) {
        console.warn("Missing supplier data or supplierAggs, using default values");
        this.setState({
          initialValues: this._getInitialValues(res.organization),
        });
        return;
      }

      // Get component weight from detailed capabilities if available, otherwise from supplierAggs
      const componentWeight = this._getComponentWeight(detailedCapabilities, suppliers.supplierAggs);
      const weightRange = this._getWeightRange(detailedCapabilities, suppliers.supplierAggs);

      this.setState({
        initialValues: {
          cage: res.organization.cageCode,
          company: res.organization.name,
          additiveManufacturingCapabilities: this._getProperty(suppliers.supplierAggs, 'Additive Manufacturing Capabilities'),
          additionalCapabilities: this._getProperty(suppliers.supplierAggs, 'Additional Capabilities'),
          alloy: this._getProperty(suppliers.supplierAggs, 'Alloy'),
          approvedSource: this._getProperty(suppliers.supplierAggs, 'Approved Source'),
          assemblyMethods: this._getProperty(suppliers.supplierAggs, 'Assembly Methods'),
          bizClass: this._getProperty(suppliers.supplierAggs, 'Business Classification'),
          castings: this._getProperty(suppliers.supplierAggs, 'Casting Process'),
          // castingWeight: this._getProperty(suppliers.supplierAggs, 'Casting Weight'),
          componentWeight,
          minMaxDimensions: this._getMinMaxDimensions(detailedCapabilities),
          coreProcesses: this._getProperty(suppliers.supplierAggs, 'Core Process'),
          dimensionCapability: this._getProperty(suppliers.supplierAggs, 'Dimension Capability'),
          forgingProcesses: this._getProperty(suppliers.supplierAggs, 'Forging Process'),
          fsc: this._getProperty(suppliers.supplierAggs, 'Federal Classification'),
          heatCapacity: this._getProperty(suppliers.supplierAggs, 'Heat Capacity'),
          industriesServed: this._getProperty(suppliers.supplierAggs, 'Industries Served'),
          lowVolume: this._getProperty(suppliers.supplierAggs, 'Low Volume', true),
          machiningEnvelope: this._getProperty(suppliers.supplierAggs, 'Machining Envelope'),
          machiningMethods: this._getProperty(suppliers.supplierAggs, 'Machining Methods'),
          machiningServices: this._getProperty(suppliers.supplierAggs, 'Machining Services'),
          material: this._getProperty(suppliers.supplierAggs, 'Material Type'),
          qualityCerts: this._getProperty(suppliers.supplierAggs, 'Quality Certification'),
          moldCastingProcess: this._getProperty(suppliers.supplierAggs, 'Mold Casting Process'),
          nonDestructiveTesting: this._getProperty(suppliers.supplierAggs, 'Non-Destructive Testing'),
          rapidTooling: this._getProperty(suppliers.supplierAggs, 'Rapid Tooling'),
          secondaryOperations: this._getProperty(suppliers.supplierAggs, 'Secondary Operations'),
          simulationCapability: this._getProperty(suppliers.supplierAggs, 'Simulation Capability', true),
          surfaceTreatments: this._getProperty(suppliers.supplierAggs, 'Surface Treatments'),
          weightRange
        },
      });
      this.setState({ isLoading: false });
    }

    _getInitialValues = (organization) => ({
      cage: organization.cageCode,
      company: organization.name,
      additionOptions: '',
      additiveManufacturingCapabilities: [],
      additionalCapabilities: [],
      alloy: [],
      approvedSource: [],
      assemblyMethods: [],
      bizClass: [],
      castings: [],
      componentWeight: '',
      minMaxDimensions: [],
      coreProcesses: [],
      dimensionCapability: [],
      forgingProcesses: [],
      fsc: [],
      heatCapacity: [],
      industriesServed: [],
      lowVolume: false,
      machiningEnvelope: [],
      machiningMethods: [],
      machiningServices: [],
      material: [],
      moldCastingProcess: [],
      nonDestructiveTesting: [],
      qualityCerts: [],
      rapidTooling: [],
      secondaryOperations: [],
      simulationCapability: false,
      surfaceTreatments: [],
      weightRange: '',
    });

    _getProperty = (object, value, checkbox = false) => {
      if (!object || !object[value]) {
        if (value === 'Simulation Capability') {
          return false;
        }
        if (value === 'Component Weight' || value === 'Weight Range') {
          return '';
        }
        return checkbox ? 'false' : value === 'Weight Range' ? '' : [];
      }

      const array = Object.keys(object[value]);
      if (value === 'Simulation Capability') {
        return array[0] === 'true' || array[0] === true;
      }
      if (value === 'Weight Range' || value === 'Component Weight') {
        return array.length ? array[0] : '';
      }
      return checkbox ? array[0] : array.length ? array : [];
    };

    _getComponentWeight = (detailedCapabilities, supplierAggs) => {
      // First try to get from detailed capabilities (REST API)
      if (detailedCapabilities) {
        // Handle REST API response format: { status: "success", data: { componentWeight: "..." } }
        if (detailedCapabilities.data && detailedCapabilities.data.componentWeight) {
          console.log('Found componentWeight in REST API response:', detailedCapabilities.data.componentWeight);
          return detailedCapabilities.data.componentWeight;
        }

        // Handle direct response format: { componentWeight: "..." }
        if (detailedCapabilities.componentWeight) {
          console.log('Found componentWeight in direct response:', detailedCapabilities.componentWeight);
          return detailedCapabilities.componentWeight;
        }
      }

      // Fallback to supplierAggs
      return this._getProperty(supplierAggs, 'Component Weight');
    };

    _getWeightRange = (detailedCapabilities, supplierAggs) => {
      // First try to get from detailed capabilities (REST API)
      if (detailedCapabilities) {
        // Handle REST API response format: { status: "success", data: { weightRange: "..." } }
        if (detailedCapabilities.data && detailedCapabilities.data.weightRange) {
          console.log('Found weightRange in REST API response:', detailedCapabilities.data.weightRange);
          return detailedCapabilities.data.weightRange;
        }

        // Handle direct response format: { weightRange: "..." }
        if (detailedCapabilities.weightRange) {
          console.log('Found weightRange in direct response:', detailedCapabilities.weightRange);
          return detailedCapabilities.weightRange;
        }
      }

      // Fallback to supplierAggs
      return this._getProperty(supplierAggs, 'Weight Range');
    };

    _getMinMaxDimensions = (detailedCapabilities) => {
      if (!detailedCapabilities) {
        return [];
      }

      // Handle REST API response format: { status: "success", data: { minMaxDimensions: [...] } }
      if (detailedCapabilities.data && detailedCapabilities.data.minMaxDimensions) {
        console.log('Found minMaxDimensions in REST API response:', detailedCapabilities.data.minMaxDimensions);
        return detailedCapabilities.data.minMaxDimensions;
      }

      // Handle direct response format: { minMaxDimensions: [...] }
      if (detailedCapabilities.minMaxDimensions) {
        console.log('Found minMaxDimensions in direct response:', detailedCapabilities.minMaxDimensions);
        return detailedCapabilities.minMaxDimensions;
      }

      // Handle GraphQL response format: { data: { minMaxDimensions: [...] } }
      if (detailedCapabilities.data && detailedCapabilities.data.data && detailedCapabilities.data.data.minMaxDimensions) {
        console.log('Found minMaxDimensions in GraphQL response:', detailedCapabilities.data.data.minMaxDimensions);
        return detailedCapabilities.data.data.minMaxDimensions;
      }

      console.log('No minMaxDimensions found in response:', detailedCapabilities);
      return [];
    };

    _onAlloyChange = async (values, material, formdata) => {
      let materialValue;
      switch (material) {
        case 'Alloy Steel': materialValue = 'alloySteel'; break;
        case 'Aluminum': materialValue = 'aluminum'; break;
        case 'Copper': materialValue = 'copper'; break;
        case 'Iron': materialValue = 'iron'; break;
        case 'Other Non-Ferrous': materialValue = 'other'; break;
        case 'Steel': materialValue = 'steel'; break;
        case 'Metal Matrix Composites': materialValue = 'mmc'; break;
        default: return;
      }

      await this.setState({ [materialValue]: values });
      const initialValues = { ...formdata };

      // Only combine alloys from materials that are actually selected
      const selectedMaterials = formdata.material || [];
      const array = [];

      if (selectedMaterials.includes('Alloy Steel')) array.push(...(this.state.alloySteel || []));
      if (selectedMaterials.includes('Aluminum')) array.push(...(this.state.aluminum || []));
      if (selectedMaterials.includes('Copper')) array.push(...(this.state.copper || []));
      if (selectedMaterials.includes('Iron')) array.push(...(this.state.iron || []));
      if (selectedMaterials.includes('Other Non-Ferrous')) array.push(...(this.state.other || []));
      if (selectedMaterials.includes('Steel')) array.push(...(this.state.steel || []));
      if (selectedMaterials.includes('Metal Matrix Composites')) array.push(...(this.state.mmc || []));

      initialValues.alloy = [...new Set(array)];
      this.setState({ initialValues });
    };

    _onMaterialChange = (values, side, formData) => {
      const initialValues = { ...formData };
      if (side === 'all') {
        initialValues.material = values;
        this.setState({ initialValues });
        return;
      }
      const leftAndRight = { left: [], right: [] };

      initialValues.material.forEach(material => {
        if (['Alloy Steel', 'Aluminum', 'Brass', 'Carbon Steel', 'Cobalt', 'Copper', 'Copper (Brass/Bronze)', 'High Temp Alloy', 'Iron', 'Lead', 'Magnesium', 'Micro Alloy'].includes(material)) {
          leftAndRight.left.push(material);
        } else {
          leftAndRight.right.push(material);
        }
      });

      leftAndRight[side] = values;
      initialValues.material = [...new Set([...leftAndRight.left, ...leftAndRight.right])];
      this.setState({ initialValues });
    };

    _setInitialValuesForAlloy = (materialValue, values) => {
      this.setState({ [materialValue]: values });
    };

    _onSubmit = async formData => {
      try {
        const resp = await updateCaps(formData, this.props.auth);
        console.log('Update response:', resp);

        await updateCapabilities(this.props.auth.organizationId, formData);

        // Handle REST API response format - iconrest returns just the status string
        if (resp === 'success') {
          message.success('Your Supplier Profile has been successfully updated.', 2);
        } else if (resp && resp.status === 'success') {
          // Handle JSON response format
          message.success('Your Supplier Profile has been successfully updated.', 2);
        } else if (resp && resp.statusCode === '200' && resp.message === 'success') {
          // Fallback for GraphQL response format
          message.success('Your Supplier Profile has been successfully updated.', 2);
        } else {
          message.error('Your Supplier Profile failed to update!', 2);
        }
      } catch (err) {
        message.error('Your Supplier Profile failed to update!', 2);
        console.error('Error updating profile:', err);
      }
    };

    render() {
      const { initialValues } = this.state;
      const { auth } = this.props;

      const pageProps = {
        auth,
        initialValues,
        onSubmit: this._onSubmit,
        onAlloyChange: this._onAlloyChange,
        onMaterialChange: this._onMaterialChange,
        setInitialValuesForAlloy: this._setInitialValuesForAlloy,
        isLoading: this.state.isLoading,
      };
      return <EditSupplierPage {...pageProps} />;
    }
  }

  return withRouter(WrappedComponent);
}


// WEBPACK FOOTER //
// ./src/components/EditSupplierCaps/EditSupplierCapsHOC.jsx